import React, { useState } from "react";
import { api } from "@/lib/api";
import { logError } from "@/lib/log";
import { Mail, MapPin, Send } from "lucide-react";
import { toast } from "sonner";

const Contact = () => {
    const [form, setForm] = useState({
        name: "",
        email: "",
        phone: "",
        subject: "",
        message: "",
    });
    const [submitting, setSubmitting] = useState(false);

    const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email || !form.message) {
            toast.error("Name, email, and message are required.");
            return;
        }
        setSubmitting(true);
        try {
            const r = await api.post("/contact", form);
            toast.success(r.data.message || "Message received.");
            setForm({
                name: "",
                email: "",
                phone: "",
                subject: "",
                message: "",
            });
        } catch (err) {
            logError(err);
            toast.error(
                err.response?.data?.detail ||
                    "Could not send message. Try again.",
            );
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <section
            id="contact"
            data-testid="contact-section"
            className="relative py-24 md:py-32 bg-[#0B0E14]"
        >
            <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
                <div className="grid lg:grid-cols-5 gap-12 lg:gap-16">
                    {/* Left - info */}
                    <div className="lg:col-span-2">
                        <div className="bs-eyebrow">Get In Touch</div>
                        <h2
                            data-testid="contact-title"
                            className="font-anton text-4xl md:text-5xl lg:text-6xl uppercase text-white tracking-wide mt-4 leading-[1.05]"
                        >
                            Hold The Line.{" "}
                            <span className="text-[#ffffff]">We'll Answer.</span>
                        </h2>
                        <div className="bs-stencil-divider my-8 max-w-xs" />
                        <p className="font-body text-slate-300 leading-relaxed">
                            Questions about a roast, an order, or wholesale?
                            Reach out — we read every message and respond
                            within one business day.
                        </p>

                        <div className="mt-10 space-y-6">
                            <ContactRow
                                icon={MapPin}
                                title="Mailing Address"
                                lines={["PO Box 16115", "Louisville, KY 40256"]}
                            />
                            <ContactRow
                                icon={Mail}
                                title="Email"
                                href="mailto:badgeandshieldcoffee@gmail.com"
                                testid="contact-email"
                                lines={["badgeandshieldcoffee@gmail.com"]}
                            />
                        </div>
                    </div>

                    {/* Right - form */}
                    <form
                        onSubmit={handleSubmit}
                        data-testid="contact-form"
                        className="lg:col-span-3 bs-card p-6 md:p-10 space-y-5"
                    >
                        <div className="grid sm:grid-cols-2 gap-4">
                            <div>
                                <label className="bs-label">Name *</label>
                                <input
                                    type="text"
                                    required
                                    value={form.name}
                                    onChange={(e) =>
                                        update("name", e.target.value)
                                    }
                                    data-testid="contact-name-input"
                                    className="bs-input"
                                />
                            </div>
                            <div>
                                <label className="bs-label">Email *</label>
                                <input
                                    type="email"
                                    required
                                    value={form.email}
                                    onChange={(e) =>
                                        update("email", e.target.value)
                                    }
                                    data-testid="contact-email-input"
                                    className="bs-input"
                                />
                            </div>
                            <div>
                                <label className="bs-label">Phone</label>
                                <input
                                    type="tel"
                                    value={form.phone}
                                    onChange={(e) =>
                                        update("phone", e.target.value)
                                    }
                                    data-testid="contact-phone-input"
                                    className="bs-input"
                                />
                            </div>
                            <div>
                                <label className="bs-label">Subject</label>
                                <input
                                    type="text"
                                    value={form.subject}
                                    onChange={(e) =>
                                        update("subject", e.target.value)
                                    }
                                    data-testid="contact-subject-input"
                                    className="bs-input"
                                    placeholder="What's it about?"
                                />
                            </div>
                        </div>
                        <div>
                            <label className="bs-label">Message *</label>
                            <textarea
                                required
                                value={form.message}
                                onChange={(e) =>
                                    update("message", e.target.value)
                                }
                                data-testid="contact-message-textarea"
                                className="bs-input min-h-[140px]"
                                placeholder="How can we help?"
                            />
                        </div>
                        <button
                            type="submit"
                            disabled={submitting}
                            data-testid="contact-submit-button"
                            className="btn-primary justify-center disabled:opacity-60"
                        >
                            {submitting ? "Sending..." : "Send Message"}
                            {!submitting && <Send className="w-4 h-4" />}
                        </button>
                    </form>
                </div>
            </div>
        </section>
    );
};

const ContactRow = ({ icon: Icon, title, lines, href, testid }) => (
    <div className="flex items-start gap-4">
        <div className="p-3 border border-[#1E293B] bg-[#151A22]">
            <Icon className="w-5 h-5 text-[#ffffff]" />
        </div>
        <div>
            <div className="font-chivo text-[10px] tracking-[0.28em] uppercase text-slate-500 mb-1">
                {title}
            </div>
            {lines.map((line, i) =>
                href ? (
                    <a
                        key={`${testid || title}-${line}-${i}`}
                        href={href}
                        data-testid={testid}
                        className="font-body block text-white hover:text-[#ffffff] transition-colors text-base"
                    >
                        {line}
                    </a>
                ) : (
                    <div
                        key={`${title}-${line}-${i}`}
                        className="font-body text-white text-base"
                    >
                        {line}
                    </div>
                ),
            )}
        </div>
    </div>
);

export default Contact;
